import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, computed, signal } from '@angular/core';
import { TranslateService, type TranslationObject } from '@ngx-translate/core';
import { normalizeStoredLanguage, readStoredLanguage, writeStoredLanguage, type SupportedLanguage } from './language-storage';

export { type SupportedLanguage } from './language-storage';

@Injectable({
  providedIn: 'root',
})
export class Translation {
  private readonly defaultLang: SupportedLanguage = 'en-US';
  private readonly supportedLangs: readonly SupportedLanguage[] = ['en-US', 'ar-SA'] as const;
  private readonly loadedLangs = new Set<SupportedLanguage>();
  private readonly pendingLoads = new Map<SupportedLanguage, Promise<void>>();

  private readonly activeLang = signal<SupportedLanguage>(this.defaultLang);

  /** Bumped each time a dictionary finishes loading. */
  readonly translationsVersion = signal(0);

  /**
   * Reactive mirror of {@link getCurrentLang}. `instant()` is not reactive, so computeds that build
   * translated view models (select options, palette groups, the avatar menu, RTL-aware tooltip positions)
   * read this signal first.
   *
   * It notifies on a language change AND whenever a dictionary finishes loading (hence the always-unequal
   * comparer): a computed that ran before the JSON arrived would otherwise cache raw keys forever, which is
   * how raw keys once ended up in select options. Reading this one signal is the whole convention.
   */
  readonly currentLang = computed<SupportedLanguage>(
    () => {
      this.translationsVersion();
      return this.activeLang();
    },
    { equal: () => false },
  );

  instant(key: string, interpolateParams?: object): string {
    return this.translateService.instant(key, interpolateParams);
  }

  constructor(
    public readonly translateService: TranslateService,
    @Inject(DOCUMENT) private document: Document,
  ) {
    this.translateService.addLangs([...this.supportedLangs]);
    this.activeLang.set(this.getCurrentLang());
    this.translateService.onLangChange.subscribe((event) => this.activeLang.set(normalizeStoredLanguage(event.lang)));
    for (const lang of this.supportedLangs) {
      void this.ensureTranslationsLoaded(lang);
    }
  }

  getCurrentLang(): SupportedLanguage {
    return normalizeStoredLanguage(
      this.translateService.getCurrentLang() ||
        this.translateService.getFallbackLang() ||
        this.defaultLang,
    );
  }

  /**
   * Applies the language the operator chose last time, before the first paint. Without this the console
   * always booted in English while the HTTP interceptors kept sending the stored language, so an Arabic
   * operator got an English, left-to-right UI filled with Arabic API data after every reload.
   *
   * Resolves once every dictionary is in (or has failed), so startup never renders raw keys. Nothing is
   * written to storage: an operator who never picked a language keeps having no stored preference.
   */
  async restoreStoredLanguage(): Promise<void> {
    this.activate(readStoredLanguage() ?? this.defaultLang);
    await Promise.all(this.supportedLangs.map((lang) => this.ensureTranslationsLoaded(lang)));
  }

  setLanguage(lang?: string, options?: { reloadPage?: boolean }): SupportedLanguage {
    const currentLang = this.getCurrentLang();
    const nextLang = lang
      ? normalizeStoredLanguage(lang)
      : this.nextLanguage(currentLang);
    const shouldReloadPage = options?.reloadPage === true && nextLang !== currentLang;

    this.activate(nextLang);
    writeStoredLanguage(nextLang);

    if (shouldReloadPage) {
      this.document.defaultView?.location.reload();
    }

    return nextLang;
  }

  /** Makes `lang` the active language everywhere except storage: ngx-translate, document direction, the signal. */
  private activate(lang: SupportedLanguage): void {
    this.translateService.setFallbackLang(this.defaultLang);
    this.translateService.use(lang);
    void this.ensureTranslationsLoaded(lang);
    this.applyDirection(lang);
    this.activeLang.set(lang);
  }

  private applyDirection(lang: SupportedLanguage): void {
    const dir = this.isRtl(lang) ? 'rtl' : 'ltr';
    this.document.documentElement.dir = dir;
    this.document.documentElement.lang = lang;
    this.document.body.dir = dir;
  }

  private isRtl(lang: SupportedLanguage): boolean {
    return lang.toLowerCase().startsWith('ar');
  }

  private nextLanguage(currentLang: SupportedLanguage): SupportedLanguage {
    const currentIndex = this.supportedLangs.indexOf(currentLang);
    return this.supportedLangs[(currentIndex + 1) % this.supportedLangs.length] ?? this.defaultLang;
  }

  private ensureTranslationsLoaded(lang: SupportedLanguage): Promise<void> {
    if (this.loadedLangs.has(lang)) {
      return Promise.resolve();
    }

    const pendingLoad = this.pendingLoads.get(lang);
    if (pendingLoad) {
      return pendingLoad;
    }

    const fetcher = this.document.defaultView?.fetch?.bind(this.document.defaultView);
    if (!fetcher) {
      return Promise.resolve();
    }

    const load = fetcher(`/assets/i18n/${lang}.json`)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Failed to load translations for ${lang}`);
        }

        return response.json() as Promise<TranslationObject>;
      })
      .then((translations) => {
        this.translateService.setTranslation(lang, translations, true);
        this.loadedLangs.add(lang);
        this.translationsVersion.update((v) => v + 1);
      })
      .catch(() => {
        // Fall back to the configured ngx-translate loader when preload fails.
      })
      .finally(() => {
        this.pendingLoads.delete(lang);
      });

    this.pendingLoads.set(lang, load);
    return load;
  }
}
